import React from 'react';
import AsyncSelect from 'react-select/async';
import { Icon } from '@lomarengas/design-system';
import { useAppDispatch } from '../../../app/hooks';
import {
  AddOnDefinitionItem,
  getAllAddOnsAsync,
} from '../../../features/addons/addonSlice';
import { LOCALES } from '../../constants';

interface AddonSelectorProps {
  Placeholder?: string;
  Subtitle: string;
  Title: string;
  onAddonSelected?: (selectedAddonId: string) => void;
}

interface OptionType {
  value: string;
  label: string;
}

const AddonSelector = ({
  Placeholder,
  Subtitle,
  Title,
  onAddonSelected,
}: AddonSelectorProps) => {
  const dispatch = useAppDispatch();

  const handleSelectAddonChange = (selectedOption: OptionType) => {
    if (selectedOption && selectedOption.label && selectedOption.value) {
      const selectedValue = selectedOption.value;
      onAddonSelected(selectedValue);
    } else {
      onAddonSelected(null);
    }
  };

  // Transform fetched options to match react-select format
  const formatOptions = (
    options: AddOnDefinitionItem[] | undefined,
  ): OptionType[] => {
    if (!options || options.length === 0) {
      return [];
    }

    return options.map(option => ({
      value: option.id,
      label: option.name,
    }));
  };

  const loadOptions = async (inputValue: string): Promise<OptionType[]> => {
    const addons = await dispatch(
      getAllAddOnsAsync({
        culture: LOCALES.Fi,
        PageNumber: 1,
        PageSize: 10000000,
      }),
    );
    // Access response data and format the options
    const formattedOptions = formatOptions(
      addons.payload.data as AddOnDefinitionItem[],
    );
    return formattedOptions.filter(option =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  return (
    <>
      <div className="d-flex align-items-start align-items-sm-center mb-24 mb-xl-32">
        <div className="d-block bg-primary fs-3 me-24 px-16 py-8 rounded text-center text-white">
          <Icon Name="tags-fill" />
        </div>
        <div className="flex-grow-1">
          <h2 className="h5 mb-0">{Title}</h2>
          <p className="text-gray-600 mb-0">{Subtitle}</p>
        </div>
      </div>
      <AsyncSelect
        styles={{
          control: baseStyles => ({
            ...baseStyles,
            borderColor: '#ced4da',
            borderRadius: 6,
          }),
          dropdownIndicator: baseStyles => ({
            ...baseStyles,
            visibility: 'hidden',
          }),
          indicatorSeparator: baseStyles => ({
            ...baseStyles,
            visibility: 'hidden',
          }),
        }}
        classNames={{
          control: state => 'form-control form-control-search ps-48 pe-8',
        }}
        inputId="tagId"
        name="tagId"
        placeholder={Placeholder}
        isClearable
        isSearchable
        loadOptions={loadOptions}
        cacheOptions
        onChange={option => handleSelectAddonChange(option)}
      />
    </>
  );
};

AddonSelector.defaultProps = {
  Placeholder: 'Hae lisäpalvelun nimellä...',
};

export default AddonSelector;
export { AddonSelector };
export type { AddonSelectorProps };
